import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ClipboardList, Plus, AlertCircle, ChevronRight } from 'lucide-react';
import apiService from '../../services/api';
import { useTreatment } from '../../context/TreatmentContext';
import { useAuth } from '../../context/AuthContext';
import TreatmentSubHeader from '../../components/TreatmentSubHeader';
import '../../styles/treatment.css';

const TreatmentPlansList = () => {
  const { currentPatient, lastRecommendation, clearLastRecommendation } = useTreatment();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchParams] = useSearchParams();
  const fromRecommendation = searchParams.get('fromRecommendation') === '1';

  const fetchPlans = useCallback(async () => {
    if (!currentPatient?.id) {
      setPlans([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getTreatmentPlansByPatientId(currentPatient.id);
      setPlans(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, [currentPatient?.id]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    if (fromRecommendation && lastRecommendation) {
      setShowCreateForm(true);
    }
  }, [fromRecommendation, lastRecommendation]);

  if (!currentPatient) {
    return (
      <div className="treatment-page">
        <section className="hero">
          <div className="container">
            <div className="hero-content text-center">
              <h1>Treatment Plans</h1>
              <p>View and manage treatment plans.</p>
            </div>
          </div>
        </section>
        <TreatmentSubHeader />
        <section className="section">
          <div className="container">
            <div className="treatment-card treatment-card-cta">
              <AlertCircle size={24} />
              <div>
                <h3>Select a patient first</h3>
                <p>Choose a patient to view their treatment plans.</p>
                <Link to="/treatment/patients" className="btn btn-primary">Go to Patients</Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="treatment-page">
      <section className="hero">
        <div className="container">
          <div className="hero-content text-center">
            <h1>Treatment Plans</h1>
            <p>Plans for {currentPatient.name}.</p>
          </div>
        </div>
      </section>
      <TreatmentSubHeader />

      <section className="section">
        <div className="container">
          <div className="treatment-breadcrumb">
            <Link to="/treatment">AYUSH Care</Link>
            <span> / Treatment Plans</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
            <h2 className="section-title" style={{ marginBottom: 0 }}>Plans</h2>
            <Link to="/treatment/recommendations" className="btn btn-primary">
              <Plus size={18} /> New plan from recommendations
            </Link>
          </div>

          {showCreateForm && lastRecommendation && (
            <CreatePlanForm
              patientId={currentPatient.id}
              recommendation={lastRecommendation}
              onSuccess={() => {
                clearLastRecommendation();
                setShowCreateForm(false);
                fetchPlans();
              }}
              onCancel={() => {
                setShowCreateForm(false);
                clearLastRecommendation();
              }}
            />
          )}

          {loading && (
            <div className="loading-state">
              <div className="spinner" />
              <p>Loading plans...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p>{error}</p>
              <button className="btn btn-outline" onClick={fetchPlans}>Retry</button>
            </div>
          )}

          {!loading && !error && plans.length === 0 && !showCreateForm && (
            <div className="treatment-empty">
              <ClipboardList size={48} style={{ color: 'var(--text-muted)', marginBottom: '1rem' }} />
              <p>No treatment plans yet. Generate recommendations first, then save as a plan.</p>
              <Link to="/treatment/recommendations" className="btn btn-primary">Get recommendations</Link>
            </div>
          )}

          {!loading && !error && plans.length > 0 && (
            <div className="treatment-quicklinks" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
              {plans.map((plan) => (
                <Link
                  key={plan.id}
                  to={`/treatment/plans/${plan.id}`}
                  className="card treatment-quicklink-card"
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3>{plan.conditionName}</h3>
                    <ChevronRight size={20} className="treatment-quicklink-arrow" />
                  </div>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    {plan.prakriti} · {plan.duration || 'No duration'}
                  </p>
                  {plan.doctorName && (
                    <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      🩺 {plan.doctorName}
                    </p>
                  )}
                  {plan.upcomingCheckupDate && (
                    <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: 'var(--accent-pink)', fontWeight: 'bold' }}>
                      📅 Next Checkup: {new Date(plan.upcomingCheckupDate).toLocaleDateString()}
                    </p>
                  )}
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    Created {plan.createdAt ? new Date(plan.createdAt).toLocaleDateString() : ''}
                  </p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

function CreatePlanForm({ patientId, recommendation, onSuccess, onCancel }) {
  const { user } = useAuth();
  const [duration, setDuration] = useState('3 months');
  const [explanation, setExplanation] = useState(recommendation?.explanation || '');
  const [doctorName, setDoctorName] = useState(user?.name || '');
  const [lastCheckupDate, setLastCheckupDate] = useState(new Date().toISOString().split('T')[0]);
  const [upcomingCheckupDate, setUpcomingCheckupDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (user?.name && !doctorName) {
      setDoctorName(user.name);
    }
  }, [user, doctorName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    try {
      const payload = {
        patientId,
        conditionId: recommendation.conditionId,
        prakriti: recommendation.prakriti || 'All',
        herbalMedicines: JSON.stringify(recommendation.recommendedMedicines || []),
        yogaAsanas: JSON.stringify(recommendation.recommendedYogaAsanas || []),
        dietaryRecommendations: JSON.stringify(recommendation.recommendedDietaryItems || []),
        lifestyleModifications: (recommendation.lifestyleModifications || []).join('; '),
        duration,
        explanation,
        confidenceScore: recommendation.confidenceScore,
        doctorName: doctorName || null,
        lastCheckupDate: lastCheckupDate ? new Date(lastCheckupDate).toISOString() : null,
        upcomingCheckupDate: upcomingCheckupDate ? new Date(upcomingCheckupDate).toISOString() : null,
      };
      await apiService.createTreatmentPlan(payload);
      onSuccess();
    } catch (err) {
      setSubmitError(err.message || 'Failed to create plan');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="treatment-card" style={{ marginBottom: '1.5rem' }}>
      <div style={{ flex: 1 }}>
        <h3>Save recommendation as treatment plan</h3>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
          {recommendation?.conditionName} for {recommendation?.patientName}
        </p>
        <form className="treatment-form" onSubmit={handleSubmit}>
          {submitError && (
            <p style={{ color: 'var(--accent-pink)', marginBottom: '0.5rem' }}><AlertCircle size={16} /> {submitError}</p>
          )}
          <div className="form-group-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Doctor Name</label>
              <input
                type="text"
                className="form-control"
                value={doctorName}
                onChange={(e) => setDoctorName(e.target.value)}
                placeholder="e.g. Dr. A. Sharma"
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Duration</label>
              <input
                type="text"
                className="form-control"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="e.g. 3 months"
              />
            </div>
          </div>

          <div className="form-group-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Last Checkup Date</label>
              <input
                type="date"
                className="form-control"
                value={lastCheckupDate}
                onChange={(e) => setLastCheckupDate(e.target.value)}
                onClick={(e) => { if (typeof e.target.showPicker === 'function') e.target.showPicker(); }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Upcoming Checkup Date</label>
              <input
                type="date"
                className="form-control"
                value={upcomingCheckupDate}
                onChange={(e) => setUpcomingCheckupDate(e.target.value)}
                onClick={(e) => { if (typeof e.target.showPicker === 'function') e.target.showPicker(); }}
              />
            </div>
          </div>


          <div className="form-group">
            <label className="form-label">Notes / Explanation</label>
            <textarea
              className="form-control"
              rows={3}
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              placeholder="Optional notes"
            />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create plan'}
            </button>
            <button type="button" className="btn btn-outline" onClick={onCancel}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TreatmentPlansList;
